/* eslint-disable indent */

import { Close } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useColorScheme,
  useMediaQuery,
} from '@mui/material';
import Slide from '@mui/material/Slide';
import PropTypes from 'prop-types';
import React from 'react';
import RRuleGenerator from 'react-rrule-generator';

import {
  useDispatch,
  useSelector,
} from 'react-redux';

import FeedAutocomplete from '../../../../../../../components/Autocompletes/Feeds';
import Horse from '../../../../../../../components/Horse';
import {
  addHorseFeed,
  deleteHorseFeed,
  updateHorseFeed,
} from '../../../../../../../redux/horse/actions';
import { primary } from '../../../../../../../utils/brandColors';
import theme from '../../../../../../../utils/theme';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const AddEditModal = ({
  open,
  onClose,
  horseId,
  horseIds,
  horseFeed,
  selectedFeed,
}) => {
  // eslint-disable-next-line no-unused-vars
  const dispatch = useDispatch();
  const fullscreen = useMediaQuery(theme.breakpoints.down('md'));
  const { mode } = useColorScheme();

  // React state
  const [notes, setNotes] = React.useState('');
  const [quantity, setQuantity] = React.useState('');
  const [quantityMeasure, setQuantityMeasure] = React.useState('');
  // eslint-disable-next-line no-unused-vars
  const [frequency, setFrequency] = React.useState({ rrule: 'RRULE:FREQ=DAILY;INTERVAL=1;WKST=SU' });
  const [feed, setFeed] = React.useState(null);
  const [feedValidation, setFeedValidation] = React.useState(null);
  const [savingError, setSavingError] = React.useState(null);

  // Redux state
  const isFetchingHorseFeedsHorse = useSelector((state) => state.horse.isFetchingHorseFeeds);
  const isFetchingHorseFeedsGlobal = useSelector((state) => state.feed.isFetchingHorseFeeds);
  const isLoading = isFetchingHorseFeedsHorse || isFetchingHorseFeedsGlobal;
  const barnHorsesMap = useSelector((state) => state.barn.barnHorsesMap);

  React.useEffect(() => {
    setSavingError(null);
    setFeed(horseFeed
      ? horseFeed.feed
      : selectedFeed || null);
    setFeedValidation(null);
    setNotes(horseFeed
      ? horseFeed.notes
      : '');
    setQuantity(horseFeed
      ? horseFeed.quantity
      : '');
    setQuantityMeasure(horseFeed
      ? horseFeed.quantityMeasure
      : '');
    setFrequency((horseFeed && horseFeed.frequency && horseFeed.frequency.rrule)
      ? horseFeed.frequency
      : { rrule: 'RRULE:FREQ=DAILY;INTERVAL=1;WKST=SU' });
  }, [horseFeed, open]);

  React.useEffect(() => {
    if (feed && feed.defaultQuantityMeasure && !quantityMeasure) {
      setQuantityMeasure(feed.defaultQuantityMeasure)
    }
  }, [feed]);

  const validateFields = (isNewFeed) => {
    if (isNewFeed && !feed) {
      setFeedValidation('Please select a feed');
      return false;
    }
    if (isNewFeed && !quantity) {
      setSavingError('Please provide a quantity');
      return false;
    }
    if (isNewFeed && !quantityMeasure) {
      setSavingError('Please select a quantity measure');
      return false;
    }
    return true;
  };

  const handleChangeFrequency = (rrule) => {
    if (rrule.indexOf('FREQ=DAILY') < 0) {
      setFrequency({
        rrule,
        quantity: 0,
        am: '',
        midday: '',
        pm: '',
        evening: '',
      })
    } else {
      setFrequency({
        rrule,
        quantity: 0,
        am: '0',
        midday: '0',
        pm: '0',
        evening: '0',
      })
    }
  };

  const handleSave = () => {
    const isNewHorseFeed = !horseFeed;
    if (!validateFields(isNewHorseFeed)) { return; }

    // Adding new feed
    if (isNewHorseFeed) {
      if (horseIds && horseIds.length > 0) {
        // multiple horses passed in so loop over call
        const promises = horseIds.map((id) => dispatch(addHorseFeed(id, feed.id, {
          notes,
          quantity,
          quantityMeasure,
          frequency,
        })))
        Promise.all(promises).then(() => {
          onClose();
        }).catch((error) => {
          setSavingError(error.message || 'There was an error attempting to add the horse feeds. Please contact Questri support.');
          console.error(error);
        });
      } else {
        dispatch(addHorseFeed(horseId, feed.id, {
          notes,
          quantity,
          quantityMeasure,
          frequency,
        })).then(() => {
          onClose();
        }).catch((error) => {
          setSavingError(error.message || 'There was an error attempting to add the horse feed. Please contact Questri support.');
          console.error(error);
        });
      }
    } else {
      dispatch(updateHorseFeed(horseId, horseFeed.feed.id, {
        notes,
        quantity,
        quantityMeasure,
        frequency,
      })).then(() => {
        onClose();
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to update the horse feed. Please contact Questri support.');
        console.error(error);
      });
    }
  };

  const handleDelete = () => {
    dispatch(deleteHorseFeed(horseId, horseFeed.feed.id)).then(() => {
      onClose();
    }).catch((error) => {
      setSavingError(error.message || 'There was an error attempting to delete the horse feed. Please contact Questri support.');
      console.error(error);
    });
  };

  return (
    <Dialog
      TransitionComponent={Transition}
      open={open}
      fullScreen={fullscreen}
      onClose={onClose}
      PaperProps={{
        sx: {
          m: 0,
          mx: fullscreen
            ? 0
            : 2,
          width: '100%',
          maxWidth: 720,
        },
      }}
      aria-labelledby="modal-add-horse-feed-title"
      aria-describedby="modal-add-horse-feed-description"
    >
      <DialogTitle
        sx={{
          background: mode === 'light'
            ? primary.main
            : 'var(--template-palette-background-paper)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          id="modal-add-horse-feed-title"
          variant="h4"
          component="span"
          color="#fff"
        >
          {horseFeed
            ? 'Edit'
            : 'Add'} Horse Feed {horseFeed && `(${horseFeed.feed.name})`}
        </Typography>
        <IconButton onClick={onClose}><Close sx={{ color: '#fff' }} /></IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ p: 2 }}
      >
        {
          horseId && barnHorsesMap[horseId]
          && (
            <Box
              sx={{ my: 2 }}
            ><Horse horse={barnHorsesMap[horseId][0].horse} />
            </Box>
          )
        }

        {
          horseIds && horseIds.length > 0 && (
            <Grid
              container
              spacing={1}
              sx={{
                mt: 1,
                mb: 2,
              }}
            >

              {
                horseIds.filter((id) => barnHorsesMap[id]).map((id) => (
                  <Grid key={`horse-chip-${id}`}><Horse chip horse={barnHorsesMap[id][0].horse} /></Grid>
                ))
              }
            </Grid>
          )
        }
        {
          // only show if adding new feed
          !horseFeed
          && (
            <Box
              sx={{
                mt: 1,
                mb: 1,
              }}
            >
              <FeedAutocomplete
                onSelect={(e, value) => { setFeed(value); }}
                value={feed}
                validation={feedValidation}
              />
            </Box>
          )
        }
        {
          frequency.rrule.indexOf('FREQ=DAILY') <= 0 && (
            <TextField
              sx={{
                mt: 1,
                mb: 1,
                mr: 1,
                width: '49%',
              }}
              id="horse-feed-quantity"
              label="Total Quantity"
              inputProps={{ inputMode: 'numeric' }}
              value={quantity}
              onChange={(e) => { setQuantity(e.target.value); }}
            />
          )
        }
        <FormControl
          fullWidth={frequency.rrule.indexOf('FREQ=DAILY') >= 0}
          sx={frequency.rrule.indexOf('FREQ=DAILY') >= 0
            ? {
              mt: 1,
              mb: 1,
            }
            : {
              mt: 1,
              mb: 1,
              width: '49%',
              // eslint-disable-next-line react/jsx-indent
            }}
        >
          <InputLabel id="horse-feed-quantity-measure">Quantity Measure</InputLabel>
          <Select
            labelId="horse-feed-quantity-measure"
            id="horse-feed-quantity-measure-select"
            value={quantityMeasure}
            label="Quantity Measure"
            onChange={(e) => { setQuantityMeasure(e.target.value); }}
          >
            <MenuItem key="measure-menu-item-cups" value="cups">cups</MenuItem>
            <MenuItem key="measure-menu-item-flakes" value="flakes">flakes</MenuItem>
            <MenuItem key="measure-menu-item-grams" value="grams">grams</MenuItem>
            <MenuItem key="measure-menu-item-lbs" value="lbs">lbs</MenuItem>
            <MenuItem key="measure-menu-item-mlcc" value="mlcc">ml/cc</MenuItem>
            <MenuItem key="measure-menu-item-oz" value="oz">oz</MenuItem>
            <MenuItem key="measure-menu-item-pills" value="pills">pills</MenuItem>
            <MenuItem key="measure-menu-item-pumps" value="pumps">pumps</MenuItem>
            <MenuItem key="measure-menu-item-quarts" value="quarts">quarts</MenuItem>
            <MenuItem key="measure-menu-item-scoops" value="scoops">scoops</MenuItem>
            <MenuItem key="measure-menu-item-syringes" value="syringes">syringes</MenuItem>
          </Select>
        </FormControl>
        {
          (frequency.rrule.indexOf('FREQ=DAILY') >= 0) && (
            <Box
              sx={{
                mt: 1,
                mb: 1,
              }}
            >
              <TextField
                sx={{
                  mt: 1,
                  mb: 1,
                  mr: 1,
                  width: 75,
                }}
                id="horse-feed-am"
                label="AM"
                inputProps={{ inputMode: 'numeric' }}
                type="number"
                value={parseFloat(frequency.am || '0', 10)}
                onChange={(e) => {
                  const am = e.target.value.toString();
                  setFrequency({
                    ...frequency,
                    am,
                  })
                  setQuantity(
                    parseFloat(am || '0', 10)
                    + parseFloat(frequency.midday || '0', 10)
                    + parseFloat(frequency.pm || '0', 10)
                    + parseFloat(frequency.evening || '0', 10),
                  );
                }}
              />
              <TextField
                sx={{
                  m: 1,
                  width: 75,
                }}
                id="horse-feed-midday"
                label="Midday"
                inputProps={{ inputMode: 'numeric' }}
                type="number"
                value={parseFloat(frequency.midday || '0', 10)}
                onChange={(e) => {
                  const midday = e.target.value.toString();
                  setFrequency({
                    ...frequency,
                    midday,
                  })
                  setQuantity(
                    parseFloat(frequency.am || '0', 10)
                    + parseFloat(midday || '0', 10)
                    + parseFloat(frequency.pm || '0', 10)
                    + parseFloat(frequency.evening || '0', 10),
                  );
                }}
              />
              <TextField
                sx={{
                  m: 1,
                  width: 75,
                }}
                id="horse-feed-pm"
                label="PM"
                inputProps={{ inputMode: 'numeric' }}
                type="number"
                value={parseFloat(frequency.pm || '0', 10)}
                onChange={(e) => {
                  const pm = e.target.value.toString();
                  setFrequency({
                    ...frequency,
                    pm,
                  })
                  setQuantity(
                    parseFloat(frequency.am || '0', 10)
                    + parseFloat(frequency.midday || '0', 10)
                    + parseFloat(pm || '0', 10)
                    + parseFloat(frequency.evening || '0', 10),
                  );
                }}
              />
              <TextField
                sx={{
                  m: 1,
                  width: 75,
                }}
                id="horse-feed-evening"
                label="Evening"
                inputProps={{ inputMode: 'numeric' }}
                type="number"
                value={parseFloat(frequency.evening || '0', 10)}
                onChange={(e) => {
                  const evening = e.target.value.toString();
                  setFrequency({
                    ...frequency,
                    evening,
                  })
                  setQuantity(
                    parseFloat(frequency.am || '0', 10)
                    + parseFloat(frequency.midday || '0', 10)
                    + parseFloat(frequency.pm || '0', 10)
                    + parseFloat(evening || '0', 10),
                  );
                }}
              />
            </Box>
          )
        }
        <TextField
          sx={{
            mt: 1,
            mb: 1,
          }}
          id="horse-feed-notes"
          label="Notes"
          multiline
          fullWidth
          rows={4}
          value={notes}
          onChange={(e) => { setNotes(e.target.value); }}
        />
        <Box
          sx={{
            mt: 1,
            mb: 1,
          }}
        >
          <RRuleGenerator
            value={frequency.rrule}
            onChange={handleChangeFrequency}
            config={{
              repeat: ['Monthly', 'Weekly', 'Daily', 'Hourly'],
              monthly: 'on the',
              end: ['Never', 'On date'],
              weekStartsOnSunday: true,
              hideStart: true,
              hideEnd: true,
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {!horseFeed && !savingError && <Box />}
        {horseFeed && <Button onClick={handleDelete} endIcon={<DeleteIcon />} disabled={isLoading}>Delete</Button>}
        {savingError && <Typography variant="caption" component="div" color="error" gutterBottom>{savingError}</Typography>}
        <Button onClick={handleSave} disabled={isLoading} variant="contained" color="primary" loading={isLoading} loadingPosition="start" startIcon={<SaveIcon />}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

AddEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  horseId: PropTypes.string,
  horseIds: PropTypes.array,
  horseFeed: PropTypes.object,
  selectedFeed: PropTypes.object,
};

AddEditModal.defaultProps = {
  horseFeed: null,
  horseId: null,
  horseIds: [],
  selectedFeed: null,
};

export default AddEditModal;
